import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  Modal,
  TextInput,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { 
  getCollections, 
  createCollection, 
  updateCollection, 
  deleteCollection 
} from '../services/collectionsService';

const CollectionsScreen = ({ navigation, route }) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [collectionName, setCollectionName] = useState('');
  const [collectionDescription, setCollectionDescription] = useState('');

  // Reagiere auf Header-Button
  useEffect(() => {
    if (route.params?.openCreateModal) {
      setShowCreateModal(true);
      // Parameter zurücksetzen
      navigation.setParams({ openCreateModal: false });
    }
  }, [route.params?.openCreateModal]);

  // Collections laden
  const loadCollections = async () => {
    try {
      setLoading(true);
      const result = await getCollections();
      if (result.success) {
        setCollections(result.data);
      } else {
        Alert.alert('Fehler', result.error);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Körbe:', error);
      Alert.alert('Fehler', 'Fehler beim Laden der Körbe');
    } finally {
      setLoading(false);
    }
  };

  // Collections beim Fokus laden
  useFocusEffect(
    useCallback(() => {
      loadCollections();
    }, [])
  );

  // Neuen Korb erstellen
  const handleCreateCollection = async () => {
    if (!collectionName.trim()) {
      Alert.alert('Fehler', 'Name ist erforderlich');
      return;
    }

    try {
      const result = await createCollection({
        name: collectionName.trim(),
        description: collectionDescription.trim() || null
      });

      if (result.success) {
        setShowCreateModal(false);
        setCollectionName('');
        setCollectionDescription('');
        loadCollections();
      } else {
        Alert.alert('Fehler', result.error);
      }
    } catch (error) {
      console.error('Fehler beim Erstellen des Korbes:', error);
      Alert.alert('Fehler', 'Fehler beim Erstellen des Korbes');
    }
  };

  // Korb bearbeiten
  const handleEditCollection = (collection) => {
    setEditingCollection(collection);
    setCollectionName(collection.name);
    setCollectionDescription(collection.description || '');
    setShowEditModal(true);
  };

  // Korb aktualisieren
  const handleUpdateCollection = async () => {
    if (!collectionName.trim()) {
      Alert.alert('Fehler', 'Name ist erforderlich');
      return;
    }

    try {
      const result = await updateCollection(editingCollection.id, {
        name: collectionName.trim(),
        description: collectionDescription.trim() || null
      });

      if (result.success) {
        setShowEditModal(false);
        setEditingCollection(null);
        setCollectionName('');
        setCollectionDescription('');
        loadCollections();
      } else {
        Alert.alert('Fehler', result.error);
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Korbes:', error);
      Alert.alert('Fehler', 'Fehler beim Aktualisieren des Korbes');
    }
  };

  // Korb löschen
  const handleDeleteCollection = (collection) => {
    if (window.confirm(`Möchtest du den Korb "${collection.name}" wirklich löschen?`)) {
      // Löschen bestätigt
      (async () => {
        try {
          const result = await deleteCollection(collection.id);
          if (result.success) {
            loadCollections();
          } else {
            alert('Fehler: ' + result.error);
          }
        } catch (error) {
          console.error('Fehler beim Löschen des Korbes:', error);
          alert('Fehler beim Löschen des Korbes');
        }
      })();
    }
  };

  // Korb öffnen (zur Artikel-Auswahl)
  const handleOpenCollection = (collection) => {
    navigation.navigate('CollectionDetail', { collectionId: collection.id, collectionName: collection.name });
  };

  // Modal schließen
  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setEditingCollection(null);
    setCollectionName('');
    setCollectionDescription('');
  };

  const renderCollection = ({ item }) => (
    <View style={styles.collectionCard}>
      <TouchableOpacity
        style={styles.collectionContent}
        onPress={() => handleOpenCollection(item)}
      >
        <View style={styles.collectionInfo}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
            <Text style={styles.collectionName}>{item.name}</Text>
            {item.description && (
              <Ionicons name="document-text" size={16} color="#2E7D32" />
            )}
          </View>
          <Text style={styles.collectionMeta}>
            {item.item_count} {item.item_count === 1 ? 'Artikel' : 'Artikel'}
            {item.created_by_name && ` • Erstellt von ${item.created_by_name}`}
          </Text>
        </View>
        <Ionicons name="chevron-forward" size={20} color="#666" />
      </TouchableOpacity>
      
      <View style={styles.collectionActions}>
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => handleEditCollection(item)}
        >
          <Ionicons name="create-outline" size={20} color="#666" />
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => handleDeleteCollection(item)}
        >
          <Ionicons name="trash-outline" size={20} color="#E53935" />
        </TouchableOpacity>
      </View>
    </View>
  );

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color="#2E7D32" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FlatList
        data={collections}
        renderItem={renderCollection}
        keyExtractor={item => item.id}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons name="basket-outline" size={64} color="#ccc" />
            <Text style={styles.emptyText}>Keine Körbe vorhanden</Text>
            <Text style={styles.emptySubtext}>Erstelle deinen ersten Themenkorb</Text>
          </View>
        }
      />

      {/* Modal zum Erstellen/Bearbeiten */}
      <Modal
        visible={showCreateModal || showEditModal}
        transparent={true}
        animationType="slide"
        onRequestClose={closeModals}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>
              {showCreateModal ? 'Neuen Korb erstellen' : 'Korb bearbeiten'}
            </Text>
            
            <TextInput
              style={styles.input}
              placeholder="Name des Korbes"
              value={collectionName}
              onChangeText={setCollectionName}
              placeholderTextColor="#999"
            />
            
            <TextInput
              style={[styles.input, styles.textArea]}
              placeholder="Beschreibung (optional)"
              value={collectionDescription}
              onChangeText={setCollectionDescription}
              placeholderTextColor="#999"
              multiline
              numberOfLines={3}
            />
            
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={closeModals}
              >
                <Text style={styles.cancelButtonText}>Abbrechen</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.saveButton]}
                onPress={showCreateModal ? handleCreateCollection : handleUpdateCollection}
              >
                <Text style={styles.saveButtonText}>
                  {showCreateModal ? 'Erstellen' : 'Speichern'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F5F5',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContent: {
    padding: 16,
  },
  collectionCard: {
    backgroundColor: '#fff',
    borderRadius: 8,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  collectionContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  collectionInfo: {
    flex: 1,
  },
  collectionName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  collectionMeta: {
    fontSize: 12,
    color: '#999',
  },
  collectionActions: {
    flexDirection: 'row',
    paddingRight: 16,
  },
  actionButton: {
    padding: 8,
    marginLeft: 4,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 50,
  },
  emptyText: {
    fontSize: 18,
    color: '#666',
    marginTop: 10,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#999',
    marginTop: 5,
    textAlign: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 20,
    width: '90%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 20,
    textAlign: 'center',
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 6,
    padding: 12,
    fontSize: 16,
    marginBottom: 16,
    backgroundColor: '#fff',
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 10,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 6,
    alignItems: 'center',
    marginHorizontal: 5,
  },
  cancelButton: {
    backgroundColor: '#f0f0f0',
  },
  saveButton: {
    backgroundColor: '#2E7D32',
  },
  cancelButtonText: {
    color: '#666',
    fontSize: 16,
    fontWeight: '500',
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '500',
  },
});

export default CollectionsScreen;
